import React from 'react';
import { versionService } from '../../services/versionService';
import { Tag, Code, GitBranch, Calendar } from 'lucide-react';

interface VersionDisplayProps {
  variant?: 'full' | 'compact' | 'badge';
  showBuildInfo?: boolean;
  className?: string;
}

export const VersionDisplay: React.FC<VersionDisplayProps> = ({
  variant = 'compact',
  showBuildInfo = false,
  className = ''
}) => {
  const versionInfo = versionService.getCurrentVersion();
  const formattedVersion = versionService.getFormattedVersion();
  const buildDate = versionService.getBuildDate();
  const badgeColor = versionService.getVersionBadgeColor();

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (variant === 'badge') {
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${className}`}
        style={{
          backgroundColor: `var(--${badgeColor}-100)`,
          color: `var(--${badgeColor}-800)`
        }}
      >
        <img
          src="/logo-nankin.svg"
          alt="Nankin"
          className="h-3 w-auto mr-1"
        />
        <Tag className="w-3 h-3 mr-1" />
        {formattedVersion}
      </span>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`flex items-center text-sm text-gray-500 ${className}`}>
        <img
          src="/logo-nankin.svg"
          alt="Nankin"
          className="h-5 w-auto mr-2"
        />
        <Code className="w-3 h-3 mr-1" />
        <span>{formattedVersion}</span>
        {showBuildInfo && versionInfo.buildInfo.commitHash && (
          <>
            <span className="mx-1">•</span>
            <GitBranch className="w-3 h-3 mr-1" />
            <span className="font-mono text-xs">
              {versionInfo.buildInfo.commitHash.substring(0, 7)}
            </span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-2 text-sm ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <img
            src="/logo-nankin.svg"
            alt="Nankin"
            className="h-4 w-auto mr-2"
          />
          <span className="text-gray-600">Versão:</span>
        </div>
        <span className="font-semibold">{formattedVersion}</span>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-gray-600">Ambiente:</span>
        <span
          className="px-2 py-0.5 rounded text-xs font-medium"
          style={{
            backgroundColor: `var(--${badgeColor}-100)`,
            color: `var(--${badgeColor}-800)`
          }}
        >
          {versionInfo.environment}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-gray-600">Data de Build:</span>
        <span className="flex items-center">
          <Calendar className="w-3 h-3 mr-1" />
          {formatDate(buildDate)} {formatTime(buildDate)}
        </span>
      </div>

      {showBuildInfo && (
        <>
          {versionInfo.buildInfo.branch && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Branch:</span>
              <span className="flex items-center">
                <GitBranch className="w-3 h-3 mr-1" />
                {versionInfo.buildInfo.branch}
              </span>
            </div>
          )}

          {versionInfo.buildInfo.commitHash && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Commit:</span>
              <span className="font-mono text-xs">
                {versionInfo.buildInfo.commitHash.substring(0, 7)}
              </span>
            </div>
          )}
        </>
      )}

      <div className="pt-2 border-t">
        <span className="text-gray-600 text-xs">Features ativas:</span>
        <div className="flex flex-wrap gap-1 mt-1">
          {Object.entries(versionInfo.features).map(([feature, enabled]) => (
            <span
              key={feature}
              className={`px-1.5 py-0.5 rounded text-xs ${
                enabled
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {feature}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};